import paths from "@/constants/paths";
import Dropdown, { Option } from "@/ui-lib/components/Dropdown";
import LockedWrapper from "@/ui-lib/components/LockedWrapper";
import { useTheme } from "@emotion/react";
import {
  faCancel,
  faEdit,
  faPencil,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { formatDate } from "@ternary/api-lib/analytics/utils/DateUtils";
import { ReportScope, ReportType } from "@ternary/api-lib/core/types/Report";
import { actions } from "@ternary/api-lib/telemetry";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Table, {
  ActionMenuButton,
} from "@ternary/api-lib/ui-lib/components/Table/Table";
import { Tooltip } from "@ternary/api-lib/ui-lib/components/Tooltip";
import React, { useMemo, useState } from "react";
import { generatePath } from "react-router-dom";
import { useActivityTracker } from "../../../context/ActivityTrackerProvider";
import useAuthenticatedUser from "../../../hooks/useAuthenticatedUser";
import useGatekeeper from "../../../hooks/useGatekeeper";
import useRefFn from "../../../hooks/useRefFn";
import Checkbox from "../../../ui-lib/components/Checkbox";
import { FavoriteButton } from "../../../ui-lib/components/FavoriteButton";
import TableTags from "../../../ui-lib/components/TableTags";
import copyText from "../copyText";
import { getScopeText } from "../utils";

interface Report {
  id: string;
  createdAt: string;
  createdByEmail: string | null;
  createdByID: string;
  updatedAt: string | null;
  updatedByEmail: string | null;
  favoritedUserIDs: string[];
  isDistributed?: boolean | null;
  name: string;
  scope: string;
  tags: string[];
  type: ReportType;
}

interface Props {
  existingTags: string[];
  highlightedTags: string[];
  isInternal?: boolean;
  isLoading: boolean;
  reports: Report[];
  selectedReportIDs: string[];
  showMultiSelect: boolean;
  onInteraction: (interaction: ReportTable.Interaction) => void;
}

type TableData = {
  id: string;
  createdAt: string;
  createdByID: string;
  isFavorited: boolean;
  isGlobal: boolean;
  isMultiSelected: boolean;
  isDistributed?: boolean | null;
  name: string;
  owner: string;
  selectedCount: number;
  shared: string;
  tags: string[];
  tagsString: string | null;
  timeLastModified: string;
  type: ReportType;
};

const defaultSortBy = {
  id: "name",
  desc: false,
};

const columnHelper = createColumnHelper<TableData>();

export function ReportTable(props: Props): JSX.Element {
  const activityTracker = useActivityTracker();
  const authenticatedUser = useAuthenticatedUser();
  const gatekeeper = useGatekeeper();
  const theme = useTheme();

  const [filteredFavorites, setFilteredFavorites] = useState<boolean>(false);

  function handleClickRow(reportID: string): void {
    props.onInteraction({
      type: ReportTable.INTERACTION_ROW_CLICKED,
      reportID,
    });
  }

  function handleFilterFavorites() {
    props.onInteraction({
      type: ReportTable.INTERACTION_FILTER_FAVORITES_CLICKED,
    });
  }

  const handleClickFavorite = useRefFn((reportID: string) => {
    props.onInteraction({
      type: ReportTable.INTERACTION_FAVORITE_REPORT_CLICKED,
      reportID,
    });
  });

  const handleClickTag = useRefFn((tag: string) => {
    props.onInteraction({
      type: ReportTable.INTERACTION_TAG_CLICKED,
      tag,
    });
  });

  const handleMultiSelectAll = useRefFn(() => {
    const selectedIDSet = new Set(props.selectedReportIDs);

    const visableIDs = props.reports
      .filter((report) => report.scope !== ReportScope.GLOBAL)
      .map((reports) => reports.id);

    const visableSelectedIDs = visableIDs.filter((id) => selectedIDSet.has(id));

    let selectedIDs: string[];
    if (visableSelectedIDs.length > 0) {
      selectedIDs = [];
    } else {
      selectedIDs = visableIDs;
    }

    props.onInteraction({
      type: ReportTable.INTERACTION_EDIT_MUTLIPLE_TAGS_CLICKED,
      reportIDs: selectedIDs,
    });
  });

  const handleMultiSelectOne = useRefFn((id: string, checked: boolean) => {
    const selectedIDSet = new Set(props.selectedReportIDs);

    if (checked) {
      selectedIDSet.add(id);
    } else {
      selectedIDSet.delete(id);
    }

    props.onInteraction({
      type: ReportTable.INTERACTION_EDIT_MUTLIPLE_TAGS_CLICKED,
      reportIDs: props.reports
        .filter((report) => selectedIDSet.has(report.id))
        .map((report) => report.id),
    });
  });

  const handleSelectSingle = useRefFn((id: string) => {
    props.onInteraction({
      type: ReportTable.INTERACTION_EDIT_SINGLE_TAGS_CLICKED,
      reportID: id,
    });
  });

  const handleDeleteReport = useRefFn((reportID: string) => {
    activityTracker.captureAction(actions.CLICK_TRE_DELETE_REPORT_FROM_TABLE);

    props.onInteraction({
      type: ReportTable.INTERACTION_DELETE_BUTTON_CLICKED,
      reportID,
    });
  });

  const handleSubscribeReport = useRefFn((reportID: string) => {
    activityTracker.captureAction(
      actions.CLICK_TRE_REPORT_SUBSCRIBE_FROM_TABLE
    );

    props.onInteraction({
      type: ReportTable.INTERACTION_SUBSCRIBE_BUTTON_CLICKED,
      reportID,
    });
  });

  const handleUpdateReport = useRefFn((reportID: string) => {
    activityTracker.captureAction(actions.CLICK_TRE_UPDATE_REPORT_FROM_TABLE);

    props.onInteraction({
      type: ReportTable.INTERACTION_UPDATE_BUTTON_CLICKED,
      reportID,
    });
  });

  function handleViewReport(reportID: string): void {
    activityTracker.captureAction(actions.VIEW_TRE_REPORT_FROM_TABLE);

    props.onInteraction({
      type: ReportTable.INTERACTION_VIEW_BUTTON_CLICKED,
      reportID,
    });
  }

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "favorite",
        cell: ({ row }) => (
          <FavoriteButton
            iconOnly
            favorited={row.original.isFavorited}
            onClick={() => handleClickFavorite(row.original.id)}
          />
        ),
        header: () => (
          <Icon
            backgroundColorOnHover={theme.report_favorite_color}
            clickable
            color={filteredFavorites ? theme.report_favorite_color : undefined}
            icon={faStar}
            size="xl"
            onClick={() => {
              setFilteredFavorites(!filteredFavorites);
              handleFilterFavorites();
            }}
          />
        ),
        meta: { align: "center", disableClick: true },
        size: 30,
      }),
      columnHelper.accessor("name", {
        header: copyText.tableHeaderName,
        size: 200,
      }),
      columnHelper.accessor("owner", {
        header: copyText.tableHeaderOwner,
        size: 200,
      }),
      columnHelper.accessor("createdAt", {
        cell: (cell) => formatDate(new Date(cell.getValue()), "MM/dd/yyyy"),
        header: copyText.tableHeaderCreatedAt,
        size: 100,
      }),
      columnHelper.accessor("timeLastModified", {
        cell: (cell) => formatDate(new Date(cell.getValue()), "MM/dd/yyyy"),
        header: copyText.tableHeaderTimeLastModified,
        size: 130,
      }),
      columnHelper.accessor("shared", {
        header: copyText.tableHeaderVisibility,
        size: 100,
      }),
      ...(props.isInternal
        ? [
            columnHelper.accessor("isDistributed", {
              meta: { align: "center" },
              header: copyText.tableHeaderIsDistributed,
              size: 100,
            }),
          ]
        : []),
      columnHelper.accessor("tags", {
        cell: (cell) => {
          const value = cell.getValue();
          if (value.length === 0) return "--";

          return (
            <TableTags
              highlightedTags={props.highlightedTags}
              tags={value}
              onClickTag={handleClickTag}
            />
          );
        },
        header: copyText.tableHeaderTags,
        size: 300,
        sortingFn: (a, b) => {
          const aTags = a.original.tagsString;
          const bTags = b.original.tagsString;

          if (aTags === null || bTags === null) {
            return aTags === bTags ? 0 : bTags === null ? -1 : 1;
          }

          return aTags < bTags ? -1 : aTags > bTags ? 1 : 0;
        },
      }),
      columnHelper.display({
        id: "editButton",
        cell: ({ row }) => {
          let disabled = false;

          switch (row.original.type) {
            case ReportType.DEFAULT:
              disabled = !gatekeeper.canUpdateReports(row.original.id);
              break;
            case ReportType.SYSTEM_COPY:
              disabled = true;
              break;
            case ReportType.SYSTEM:
              disabled = !gatekeeper.canUpdateSystemReport;
          }

          return props.showMultiSelect ? (
            <Box onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={row.original.isMultiSelected}
                disabled={disabled}
                onChange={(e) => {
                  e.preventDefault();

                  if (disabled) return;

                  handleMultiSelectOne(
                    row.original.id,
                    e.currentTarget.checked
                  );
                }}
              />
            </Box>
          ) : (
            <Button
              disabled={disabled}
              iconStart={<Icon icon={faPencil} />}
              secondary
              size="tiny"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectSingle(row.original.id);
              }}
            />
          );
        },
        header: (headerContext) => {
          const rows = headerContext.table.getRowModel().rows;

          const checkedCount = rows.reduce(
            (count, row) => (row.original.isMultiSelected ? count + 1 : count),
            0
          );

          return props.showMultiSelect ? (
            <Checkbox
              checked={checkedCount > 0 && checkedCount === rows.length}
              dashed={checkedCount > 0}
              onChange={handleMultiSelectAll}
            />
          ) : (
            <Flex
              alignItems="center"
              height={theme.fontSize_base}
              justifyContent="space-between"
              width="100%"
            >
              <Tooltip content={copyText.tableHeaderEditTags}>
                <Button
                  iconStart={<Icon icon={faEdit} />}
                  size="tiny"
                  onClick={(e) => {
                    e.preventDefault();
                    props.onInteraction({
                      type: ReportTable.INTERACTION_DISPLAY_MULTI_SELECT_CLICKED,
                      display: !props.showMultiSelect,
                    });
                  }}
                />
              </Tooltip>
            </Flex>
          );
        },
        meta: { disableClick: true },
        size: 50,
      }),
      columnHelper.display({
        id: "actionMenu",
        cell: ({ row }) => {
          return (
            <ReportDropdown
              createdByID={row.original.createdByID}
              reportID={row.original.id}
              disabled={
                gatekeeper.canUpdateReports(row.original.id) &&
                !gatekeeper.getCanDeleteSpecificReport(row.original.createdByID)
              }
              isGlobal={row.original.isGlobal}
              isSystemReportCopy={row.original.type === ReportType.SYSTEM_COPY}
              onInteraction={props.onInteraction}
            />
          );
        },
        meta: { align: "center" },
        size: 40,
      }),
      ...(props.showMultiSelect
        ? [
            columnHelper.display({
              id: "multiSelectToggle",
              header: function renderMultiEditButton(header) {
                const rows = header.table.getRowModel().rows;

                const selectedCount = rows[0]?.original.selectedCount ?? 0;

                if (selectedCount > 0) {
                  return (
                    <Flex
                      alignItems="center"
                      height={theme.fontSize_base}
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Tooltip
                        content={copyText.reportListEditNTagsToolTipMessage.replace(
                          "%NUMBER%",
                          selectedCount.toString()
                        )}
                      >
                        <Button
                          iconStart={<Icon icon={faEdit} />}
                          primary
                          size="tiny"
                          onClick={(e) => {
                            e.preventDefault();

                            props.onInteraction({
                              type: ReportTable.INTERACTION_EDIT_MULTI_SELECT_CLICKED,
                            });
                          }}
                        />
                      </Tooltip>
                    </Flex>
                  );
                }

                return (
                  <Flex
                    alignItems="center"
                    height={theme.fontSize_base}
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Tooltip
                      content={copyText.reportListCancelEditToolTipMessage}
                    >
                      <Button
                        iconStart={<Icon icon={faCancel} />}
                        secondary
                        size="tiny"
                        onClick={(e) => {
                          e.preventDefault();

                          props.onInteraction({
                            type: ReportTable.INTERACTION_DISPLAY_MULTI_SELECT_CLICKED,
                            display: false,
                          });
                        }}
                      />
                    </Tooltip>
                  </Flex>
                );
              },
              size: 35,
            }),
          ]
        : []),
      ...(props.isInternal
        ? [
            columnHelper.display({
              id: "internalButton",
              cell: ({ row }) => (
                <Button
                  disabled={!gatekeeper.canUpdateSystemReport}
                  secondary
                  size="small"
                  width={130}
                  onClick={(event) => {
                    event.stopPropagation();

                    if (row.original.isDistributed) {
                      props.onInteraction({
                        type: ReportTable.INTERACTION_REMOVE_REPORT_BUTTON_CLICKED,
                        reportID: row.original.id,
                      });
                    } else {
                      props.onInteraction({
                        type: ReportTable.INTERACTION_DISTRIBUTE_REPORT_BUTTON_CLICKED,
                        reportID: row.original.id,
                      });
                    }
                  }}
                >
                  {row.original.isDistributed
                    ? copyText.actionMenuItemRemoveSystemReport
                    : copyText.actionMenuItemDistributeSystemReport}
                </Button>
              ),
              meta: { align: "center" },
              size: 140,
            }),
          ]
        : []),
    ],
    [
      filteredFavorites,
      props.highlightedTags,
      props.selectedReportIDs,
      props.showMultiSelect,
      props.reports,
    ]
  );

  const tableData = useMemo(
    () =>
      props.reports.map((report): TableData => {
        const sortedTags = [...report.tags].sort((a, b) => {
          if (a.toLowerCase() < b.toLowerCase()) {
            return -1;
          }
          if (a.toLowerCase() > b.toLowerCase()) {
            return 1;
          }
          return 0;
        });

        return {
          id: report.id,
          createdAt: report.createdAt,
          createdByID: report.createdByID,
          isFavorited: report.favoritedUserIDs.includes(authenticatedUser.id),
          isGlobal: report.scope === ReportScope.GLOBAL,
          isMultiSelected: props.selectedReportIDs.includes(report.id),
          isDistributed: report.isDistributed,
          name: report.name,
          owner: report.createdByEmail ?? "--",
          selectedCount: props.selectedReportIDs.length,
          shared: getScopeText(report.scope),
          tags: sortedTags,
          tagsString: sortedTags.length
            ? sortedTags.join("-").toLowerCase()
            : null,
          timeLastModified: report.updatedAt ?? report.createdAt,
          type: report.type,
        };
      }),
    [props.reports, props.selectedReportIDs]
  );

  return (
    <Table
      clickableRows
      columns={columns}
      rowHeight="70px"
      data={tableData}
      initialState={{ pagination: { pageSize: 10 }, sorting: [defaultSortBy] }}
      isLoading={props.isLoading}
      showPagination
      sortable
      onClick={(row) => handleClickRow(row.id)}
    />
  );

  interface ReportDropdownProps {
    createdByID: string;
    reportID: string;
    disabled: boolean;
    isGlobal: boolean;
    isSystemReportCopy: boolean;
    onInteraction: (interaction: ReportTable.Interaction) => void;
  }

  function ReportDropdown(dropdownProps: ReportDropdownProps): JSX.Element {
    const options: Option[] = [
      {
        href: generatePath(paths._reportBuilder, {
          reportID: dropdownProps.reportID,
        }),
        label: copyText.actionMenuItemViewReport,
        locked: false,
        tooltipText: copyText.actionMenuItemControlClickTooltip,
        onClick: () => {
          handleViewReport(dropdownProps.reportID);
        },
      },
    ];

    // If System report copy, should not be able to edit or delete.
    if (!dropdownProps.isSystemReportCopy) {
      options.push(
        {
          label: copyText.actionMenuItemEditReport,
          locked: !gatekeeper.canUpdateReports,
          onClick: () => {
            handleUpdateReport(dropdownProps.reportID);
          },
        },
        {
          label: copyText.actionMenuItemDeleteReport,
          locked: !gatekeeper.getCanDeleteSpecificReport(
            dropdownProps.createdByID
          ),
          onClick: () => {
            handleDeleteReport(dropdownProps.reportID);
          },
        }
      );
    }

    options.push({
      label: copyText.actionMenuItemSubscribeToReport,
      locked: !gatekeeper.canUpdateReports,
      onClick: () => {
        handleSubscribeReport(dropdownProps.reportID);
      },
    });

    return (
      <LockedWrapper locked={dropdownProps.disabled}>
        <Dropdown
          disabled={dropdownProps.disabled}
          options={options}
          placement="bottom-end"
        >
          <ActionMenuButton />
        </Dropdown>
      </LockedWrapper>
    );
  }
}

ReportTable.INTERACTION_DISPLAY_MULTI_SELECT_CLICKED =
  `ReportList.INTERACTION_DISPLAY_MULTI_SELECT_CLICKED` as const;
ReportTable.INTERACTION_EDIT_MULTI_SELECT_CLICKED =
  `ReportList.INTERACTION_EDIT_MULTI_SELECT_CLICKED` as const;
ReportTable.INTERACTION_EDIT_MUTLIPLE_TAGS_CLICKED =
  `ReportList.INTERACTION_EDIT_MUTLIPLE_TAGS_CLICKED` as const;
ReportTable.INTERACTION_EDIT_SINGLE_TAGS_CLICKED =
  `ReportList.INTERACTION_EDIT_SINGLE_TAGS_CLICKED` as const;
ReportTable.INTERACTION_FAVORITE_REPORT_CLICKED =
  `ReportList.INTERACTION_FAVORITE_REPORT_CLICKED` as const;
ReportTable.INTERACTION_FILTER_FAVORITES_CLICKED =
  `ReportList.INTERACTION_FILTER_FAVORITES_CLICKED` as const;
ReportTable.INTERACTION_DISTRIBUTE_REPORT_BUTTON_CLICKED =
  `ReportList.INTERACTION_DISTRIBUTE_REPORT_BUTTON_CLICKED` as const;
ReportTable.INTERACTION_ROW_CLICKED =
  `ReportList.INTERACTION_ROW_CLICKED` as const;
ReportTable.INTERACTION_TAG_CLICKED =
  `ReportList.INTERACTION_TAG_CLICKED` as const;
ReportTable.INTERACTION_REMOVE_REPORT_BUTTON_CLICKED =
  `ReportList.INTERACTION_REMOVE_REPORT_BUTTON_CLICKED` as const;
ReportTable.INTERACTION_VIEW_BUTTON_CLICKED =
  `ReportList.INTERACTION_VIEW_BUTTON_CLICKED` as const;
ReportTable.INTERACTION_UPDATE_BUTTON_CLICKED =
  `ReportList.INTERACTION_UPDATE_BUTTON_CLICKED` as const;
ReportTable.INTERACTION_DELETE_BUTTON_CLICKED =
  `ReportList.INTERACTION_DELETE_BUTTON_CLICKED` as const;
ReportTable.INTERACTION_SUBSCRIBE_BUTTON_CLICKED =
  `ReportList.INTERACTION_SUBSCRIBE_BUTTON_CLICKED` as const;

type InteractionDisplayMultiSelectClicked = {
  type: typeof ReportTable.INTERACTION_DISPLAY_MULTI_SELECT_CLICKED;
  display: boolean;
};

type InteractionEditMultipleTagsClicked = {
  type: typeof ReportTable.INTERACTION_EDIT_MUTLIPLE_TAGS_CLICKED;
  reportIDs: string[];
};

type InteractionEditMultiSelectClicked = {
  type: typeof ReportTable.INTERACTION_EDIT_MULTI_SELECT_CLICKED;
};

type InteractionEditSingleTagsClicked = {
  type: typeof ReportTable.INTERACTION_EDIT_SINGLE_TAGS_CLICKED;
  reportID: string;
};

type InteractionFavoriteReportClicked = {
  type: typeof ReportTable.INTERACTION_FAVORITE_REPORT_CLICKED;
  reportID: string;
};

type InteractionFilterFavoritesClicked = {
  type: typeof ReportTable.INTERACTION_FILTER_FAVORITES_CLICKED;
};

type InteractionDistributeReportButtonClicked = {
  type: typeof ReportTable.INTERACTION_DISTRIBUTE_REPORT_BUTTON_CLICKED;
  reportID: string;
};

type InteractionRowClicked = {
  type: typeof ReportTable.INTERACTION_ROW_CLICKED;
  reportID: string;
};

type InteractionTagClicked = {
  type: typeof ReportTable.INTERACTION_TAG_CLICKED;
  tag: string;
};

type InteractionRemoveReportButtonClicked = {
  type: typeof ReportTable.INTERACTION_REMOVE_REPORT_BUTTON_CLICKED;
  reportID: string;
};

type InteractionDeleteReportButtonClicked = {
  type: typeof ReportTable.INTERACTION_DELETE_BUTTON_CLICKED;
  reportID: string;
};

type InteractionSubscribeReportButtonClicked = {
  type: typeof ReportTable.INTERACTION_SUBSCRIBE_BUTTON_CLICKED;
  reportID: string;
};
type InteractionUpdateReportButtonClicked = {
  type: typeof ReportTable.INTERACTION_UPDATE_BUTTON_CLICKED;
  reportID: string;
};

type InteractionViewReportButtonClicked = {
  type: typeof ReportTable.INTERACTION_VIEW_BUTTON_CLICKED;
  reportID: string;
};

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ReportTable {
  export type Interaction =
    | InteractionDisplayMultiSelectClicked
    | InteractionEditMultipleTagsClicked
    | InteractionEditMultiSelectClicked
    | InteractionEditSingleTagsClicked
    | InteractionFavoriteReportClicked
    | InteractionFilterFavoritesClicked
    | InteractionDistributeReportButtonClicked
    | InteractionRowClicked
    | InteractionTagClicked
    | InteractionRemoveReportButtonClicked
    | InteractionViewReportButtonClicked
    | InteractionUpdateReportButtonClicked
    | InteractionDeleteReportButtonClicked
    | InteractionSubscribeReportButtonClicked;
}

export default ReportTable;
